import { useContext } from "react";
import { PracticesContext } from "../../contexts/contextExporter";
import { IPractice } from "../../types/generalTypes";
import PracticesListItem from "./PracticesListItem";
import { X, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { qoPartPracticesHistory } from "../../utils/queryOptions";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";

export default function PracticesList({
  items,
  userId,
  isOpen,
  onClose,
}: {
  items: IPractice[];
  userId: number;
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    setPracticeIndex,
    practiceIndex,
    nextPractice,
    prevPractice,
    isFirstPractice,
    isLastPractice,
  } = useContext(PracticesContext);

  const { data: practicesHistory } = useQuery(
    qoPartPracticesHistory({
      part_id: items[0]?.unit_ml_part_id,
      user_id: userId,
    })
  );

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-20 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={clsx(
          "w-56 h-full flex flex-col border-r border-white/[0.06]",
          "fixed lg:relative z-30 lg:z-auto transition-transform duration-300 max-lg:bg-surface-1",
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 shrink-0">
          <span className="text-sm font-semibold text-white/50">
            Sorular
            <span className="text-white/25 ml-1.5 font-normal">
              {practiceIndex + 1}/{items.length}
            </span>
          </span>
          <button
            onClick={onClose}
            className="lg:hidden p-1 text-white/30 hover:text-white/60 transition-colors cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-auto px-3">
          <div className="space-y-1">
            {items.map((practice, order) => (
              <PracticesListItem
                key={practice.id}
                item={practice}
                isActive={order === practiceIndex}
                handleClick={() => {
                  setPracticeIndex(order);
                  onClose();
                }}
                prevMaxScore={practicesHistory?.[practice.id] || null}
                order={order}
              />
            ))}
          </div>
        </div>

        {/* Nav buttons */}
        <div className="shrink-0 px-3 pb-3 pt-2 flex gap-2">
          <button
            className="cursor-pointer bg-white/5 border border-white/[0.06] hover:bg-white/10 text-white/50 py-2 rounded-lg text-xs disabled:text-white/15 disabled:cursor-not-allowed transition-colors font-medium flex-1 flex items-center justify-center gap-1"
            onClick={prevPractice}
            disabled={isFirstPractice}
          >
            <ArrowLeft size={12} weight="bold" />
            Önceki
          </button>
          <button
            className="cursor-pointer bg-white/5 border border-white/[0.06] hover:bg-white/10 text-white/50 py-2 rounded-lg text-xs disabled:text-white/15 disabled:cursor-not-allowed transition-colors font-medium flex-1 flex items-center justify-center gap-1"
            onClick={nextPractice}
            disabled={isLastPractice}
          >
            Sonraki
            <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      </div>
    </>
  );
}
